import React from 'react'
import { Route, Switch, useRouteMatch } from 'react-router'

import { isTouchDevice, shortcuts, useSearch } from '@sketch/utils'
import { routes, RouteParams, useFlag } from '@sketch/modules-common'

import {
  Dropdown,
  Tooltip,
  TooltipShortcut,
  MenuIconButton,
  useBreakpoint,
  useResponsiveDropdown,
} from '@sketch/components'

import { ReactComponent as Checkmark } from '@sketch/icons/img-checkmark-24'

// Graphql
import {
  useGetDocumentListSettingsQuery,
  useGetWorkspaceQuery,
} from '@sketch/gql-types'

import {
  IconButton,
  StyledCheckbox,
  StyledDropdownHeader,
  StyledDropdownItem,
  StyledResetIcon,
  Filter as SearchInput,
  FilterIcon,
  StyledDivider,
} from './SearchBar.styles'
import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { useSelectionContext } from '../../../storageItems/components/SelectionContext'

const ROUTES_WITH_FILTERS = [
  routes.WORKSPACE_SHARES.template(),
  routes.WORKSPACE_DRAFTS.template(),
  routes.WORKSPACE_LIBRARIES.template(),
  routes.WORKSPACE_SHARED_WITH_ME.template(),
  routes.WORKSPACE_TEMPLATES.template(),
  routes.WORKSPACE_PROJECT.template(),
  routes.WORKSPACE_TRASH_PROJECT.template(),
]

export const getPlaceholderByPath = (path: string) => {
  switch (path) {
    case routes.WORKSPACE_TRASH.template():
      return 'Search all documents in Trash'
    case routes.WORKSPACE_LIBRARIES.template():
      return 'Search Libraries'
    case routes.WORKSPACE_SHARED_WITH_ME.template():
      return 'Search Shared with Me'
    case routes.WORKSPACE_TEMPLATES.template():
      return 'Search Templates'
    case routes.WORKSPACE_ARCHIVE.template():
      return 'Search archived folders'
    case routes.WORKSPACE_DRAFTS.template():
      return 'Search My Drafts'
    default:
      return 'Search'
  }
}

interface FilterDropdownProps {
  userCanToggleProjects: boolean
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  userCanToggleProjects,
}: FilterDropdownProps) => {
  const { params } = useRouteMatch<RouteParams<'WORKSPACE_SHARES'>>()

  const { filters, hasFilters, toggleFilterFlag, clearFilters } =
    useStorageItemsFilters()

  const workspaceId = params.workspaceId

  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { identifier: workspaceId },
    fetchPolicy: 'cache-only',
  })

  const isArchiveEnabled =
    workspaceData?.workspace.features.archiveEnabled ?? false

  const reset = () => {
    clearFilters()
  }

  const resetActive = hasFilters

  return (
    <>
      <StyledDropdownHeader>
        <span>Documents</span>
        <Tooltip content="Reset" placement="top" disableWhenTouchDevice>
          <IconButton onClick={reset}>
            <StyledResetIcon $active={resetActive} />
          </IconButton>
        </Tooltip>
      </StyledDropdownHeader>

      <StyledDropdownItem>
        <StyledCheckbox
          checked={!filters.documents}
          label="All Documents"
          onChange={() => clearFilters()}
        />
      </StyledDropdownItem>

      <StyledDropdownItem>
        <StyledCheckbox
          checked={!!filters.documents?.withPrototypes}
          label="With Prototypes"
          onChange={() => toggleFilterFlag('with-prototypes')}
        />
      </StyledDropdownItem>

      <StyledDropdownItem>
        <StyledCheckbox
          checked={!!filters.documents?.userCanEdit}
          label="You can edit"
          onChange={() => toggleFilterFlag('user-can-edit')}
        />
      </StyledDropdownItem>

      <Switch>
        <Route
          path={[
            routes.WORKSPACE_SHARES.template(),
            routes.WORKSPACE_LIBRARIES.template(),
            routes.WORKSPACE_SHARED_WITH_ME.template(),
            routes.WORKSPACE_TEMPLATES.template(),
          ]}
        >
          {userCanToggleProjects && (
            <>
              <Dropdown.Divider />

              <StyledDropdownItem>
                <StyledCheckbox
                  checked={filters.includeDocumentsInProjects}
                  label="Include documents in folders"
                  onChange={() =>
                    toggleFilterFlag('ignore-documents-in-projects')
                  }
                />
              </StyledDropdownItem>

              {isArchiveEnabled && (
                <StyledDropdownItem>
                  <StyledCheckbox
                    checked={filters.includeArchivedDocuments}
                    label="Include archived documents"
                    onChange={() =>
                      toggleFilterFlag('include-archived-documents')
                    }
                  />
                </StyledDropdownItem>
              )}
            </>
          )}
        </Route>

        <Route
          path={[
            routes.WORKSPACE_PROJECT.template(),
            routes.WORKSPACE_TRASH_PROJECT.template(),
            routes.WORKSPACE_DRAFTS.template(),
          ]}
          exact
        >
          <Dropdown.Divider />
          <StyledDropdownItem>
            <StyledCheckbox
              checked={!filters.searchIgnoringNestedFolders}
              label="Search in Folders"
              help="Documents in folders will appear in search results."
              onChange={() =>
                toggleFilterFlag('search-ignoring-nested-folders')
              }
            />
          </StyledDropdownItem>
        </Route>
      </Switch>
    </>
  )
}

interface FiltersProps extends FilterDropdownProps {
  showDivider?: boolean
}

export const Filters = (props: FiltersProps) => {
  const { showDivider, userCanToggleProjects } = props
  const isMultipleSelectionOn = useFlag('multiple-selection')
  const { toggleShowCheckbox, showSelectionCheckbox } = useSelectionContext()
  const { path } = useRouteMatch()

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: FilterDropdown,
    dropdownProps: { userCanToggleProjects },
  })

  const { data } = useGetDocumentListSettingsQuery()
  const isGridLayout = data?.documentsLayout === 'GRID'

  const showFilters = ROUTES_WITH_FILTERS.find(route => path.includes(route))

  const isMobile = !useBreakpoint('sm')
  const touchDevice = isTouchDevice()

  const showSelectionCheckboxButton =
    (isMobile || touchDevice) && isMultipleSelectionOn && isGridLayout

  return (
    <>
      {showFilters && (
        <MenuIconButton
          data-testid="filter-dropdown"
          {...buttonProps}
          active={buttonProps['aria-expanded'] === 'true'}
          description="Filter"
        >
          <FilterIcon role="img" />
        </MenuIconButton>
      )}
      {content}

      {showDivider && showFilters && showSelectionCheckboxButton && (
        <StyledDivider />
      )}

      {/**
       * This empty span is to keep the space between the SearchBar and the Filter button
       * consistent when the divider is not shown because of internal rules of button
       */}
      <span />

      {showSelectionCheckboxButton && (
        <MenuIconButton
          active={showSelectionCheckbox}
          onClick={() => toggleShowCheckbox()}
        >
          <Checkmark role="img" />
        </MenuIconButton>
      )}
    </>
  )
}

export interface SearchBarProps {
  fullWidth?: boolean
}

export const SearchBar: React.FCC<SearchBarProps> = ({
  fullWidth,
}: SearchBarProps) => {
  const { search, setSearch } = useSearch()
  const { path } = useRouteMatch()

  const inputPlaceholder = getPlaceholderByPath(path)

  return (
    <Tooltip
      content={
        <>
          Search
          <TooltipShortcut>
            Press {shortcuts.search.toUpperCase()}
          </TooltipShortcut>
        </>
      }
      disableWhenTouchDevice
      placement="bottom-start"
    >
      {tooltipProps => (
        <SearchInput
          onChange={setSearch}
          value={search}
          name="search"
          placeholder={inputPlaceholder}
          fullWidth={!!fullWidth}
          {...tooltipProps}
          ref={tooltipProps.ref as any}
        />
      )}
    </Tooltip>
  )
}
